import fetch from '../utils/authenticatedFetch';
import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import {
  FaClipboardList,
  FaFilter,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaUserPlus,
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import './WorkflowConfiguration.css';

type ApiResponse<T> = {
  success: boolean;
  message: string;
  data: T;
};

type User = {
  id: number;
  name?: string;
  surname?: string;
  email?: string;
  userType?: string;
  status?: string;
  facultyId?: number;
  faculty?: string;
  departmentId?: number;
  department?: string;
};

type Alternate = {
  alternateUserId?: number;
  alternateUserName?: string;
  priorityOrder?: number;
  active?: boolean;
};

type Responsibility = {
  primaryUserId?: number;
  primaryUserName?: string;
  active?: boolean;
  alternatesMayActWhenPrimaryActive?: boolean;
  alternates?: Alternate[];
};

type WorkflowStage = {
  id?: number;
  stageCode?: string;
  name?: string;
  description?: string;
  displayOrder: number;
  actorRole: string;
  stageActionType: 'RECOMMENDATION' | 'APPROVAL';
  finalStage: boolean;
  standardDeadlineDays: number;
  active: boolean;
  responsibility: Responsibility;
};

type WorkflowConfigurationDto = {
  workflowKey: string;
  workflowName: string;
  workflowVersion?: number;
  applicationType?: string;
  scopeType: string;
  facultyId?: number;
  departmentId?: number;
  instituteCode?: string;
  committeeCode?: string;
  active?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
  resubmissionPolicy?: string;
  changedByUserId?: number;
  stages: WorkflowStage[];
};

type AuditRow = {
  id: number;
  workflowVersion?: number;
  changeType?: string;
  changedByUserName?: string;
  changedAt?: string;
};

type ValidationErrors = Record<string, string>;

const ROLE_LABELS: Record<string, string> = {
  DEPARTMENT: 'Department Representative',
  FACULTY_RAP: 'Faculty Representative',
  INSTITUTE_REPRESENTATIVE: 'Institute Representative',
  RESEARCH_CHAIR: 'Research Chair',
  PROFESSOR: 'Professor',
  REVIEWER: 'Research Ethics Committee Secretary',
  ADMIN: 'Executive Director RID',
  RESEARCH_OFFICER: 'Research Officer',
  ADMIN_CYBER: 'System Admin',
  FACULTY_ADMIN: 'Faculty Admin',
};

const ROLE_OPTIONS = [
  'DEPARTMENT',
  'FACULTY_RAP',
  'INSTITUTE_REPRESENTATIVE',
  'RESEARCH_CHAIR',
  'PROFESSOR',
  'REVIEWER',
  'ADMIN',
  'RESEARCH_OFFICER',
  'FACULTY_ADMIN',
];

const SCOPE_OPTIONS = ['GLOBAL', 'DEPARTMENT', 'FACULTY', 'INSTITUTE', 'COMMITTEE'];
const APPLICATION_TYPES = ['', 'FACULTY', 'INSTITUTE', 'CENTRE'];
const RESUBMISSION_POLICIES = ['RETURN_TO_REJECTING_STAGE', 'RESTART_WORKFLOW'];

const blankStage = (order: number): WorkflowStage => ({
  displayOrder: order,
  actorRole: 'DEPARTMENT',
  stageActionType: order === 1 ? 'RECOMMENDATION' : 'APPROVAL',
  finalStage: false,
  standardDeadlineDays: 14,
  active: true,
  responsibility: {
    active: true,
    alternatesMayActWhenPrimaryActive: false,
    alternates: [],
  },
});

const blankWorkflow = (): WorkflowConfigurationDto => ({
  workflowKey: '',
  workflowName: '',
  applicationType: '',
  scopeType: 'GLOBAL',
  active: true,
  resubmissionPolicy: 'RETURN_TO_REJECTING_STAGE',
  stages: [{ ...blankStage(1), finalStage: true }],
});

const userLabel = (user?: User) => {
  if (!user) return '';
  const name = [user.name, user.surname].filter(Boolean).join(' ').trim();
  return name || user.email || `User ${user.id}`;
};

const roleLabel = (role?: string) => (role ? ROLE_LABELS[role] || role : '');

const getErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (payload?.fieldErrors) {
      return Object.values(payload.fieldErrors).join(' ');
    }
    return payload?.message || payload?.error || response.statusText;
  } catch {
    return response.statusText;
  }
};

const WorkflowConfiguration: React.FC = () => {
  const { account } = useAuth();
  const currentUserId = Number((account as any)?.id || (account as any)?.userId || 0);
  const currentRole = String((account as any)?.userType || '').toUpperCase();
  const canManage = ['ADMIN', 'ADMIN_CYBER', 'RESEARCH_OFFICER'].includes(currentRole);

  const [configurations, setConfigurations] = useState<WorkflowConfigurationDto[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [form, setForm] = useState<WorkflowConfigurationDto>(blankWorkflow);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<ValidationErrors>({});

  const activeUsers = useMemo(
    () => users.filter((user) => String(user.status || '').toUpperCase() === 'ACTIVE'),
    [users]
  );

  const filteredConfigurations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return configurations;
    return configurations.filter((item) =>
      [item.workflowKey, item.workflowName, item.scopeType, item.instituteCode]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [configurations, search]);

  const fetchJson = async <T,>(url: string): Promise<T> => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(await getErrorMessage(response));
    const payload = (await response.json()) as ApiResponse<T>;
    return payload.data;
  };

  const load = async () => {
    if (!currentUserId || !canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const [configs, userList] = await Promise.all([
        fetchJson<WorkflowConfigurationDto[]>(`/v1/api/workflow-configurations?userId=${currentUserId}`),
        fetchJson<User[]>(`/v1/api/workflow-configurations/users?userId=${currentUserId}`),
      ]);
      setConfigurations(configs);
      setUsers(userList);
      if (configs.length > 0) {
        selectConfiguration(configs[0], false);
      }
    } catch (err: any) {
      setMessage(err?.message || 'Workflow configuration could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, canManage]);

  const loadAudit = async (workflowKey: string) => {
    if (!workflowKey || !currentUserId) return;
    try {
      const rows = await fetchJson<AuditRow[]>(
        `/v1/api/workflow-configurations/${encodeURIComponent(workflowKey)}/audit?userId=${currentUserId}`
      );
      setAudit(rows);
    } catch {
      setAudit([]);
    }
  };

  const selectConfiguration = (configuration: WorkflowConfigurationDto, shouldLoadAudit = true) => {
    const clone = JSON.parse(JSON.stringify(configuration)) as WorkflowConfigurationDto;
    clone.stages = (clone.stages || []).map((stage, index) => ({
      ...blankStage(index + 1),
      ...stage,
      responsibility: {
        active: true,
        alternates: [],
        ...(stage.responsibility || {}),
      },
    }));
    setSelectedKey(clone.workflowKey);
    setForm(clone);
    setErrors({});
    if (shouldLoadAudit) loadAudit(clone.workflowKey);
  };

  const createNew = () => {
    setSelectedKey('');
    setForm(blankWorkflow());
    setAudit([]);
    setErrors({});
    setMessage('');
  };

  const applyGenderTemplate = () => {
    setSelectedKey('GENDER_INSTITUTE');
    setForm({
      ...blankWorkflow(),
      workflowKey: 'GENDER_INSTITUTE',
      workflowName: 'Gender Institute Workflow',
      applicationType: 'CENTRE',
      scopeType: 'INSTITUTE',
      instituteCode: 'GENDER_INSTITUTE',
      stages: [
        { ...blankStage(1), actorRole: 'INSTITUTE_REPRESENTATIVE', stageActionType: 'RECOMMENDATION', name: 'Institute Representative', finalStage: false },
        { ...blankStage(2), actorRole: 'RESEARCH_CHAIR', stageActionType: 'RECOMMENDATION', name: 'Research Chair', finalStage: false },
        { ...blankStage(3), actorRole: 'PROFESSOR', stageActionType: 'APPROVAL', name: 'Professor', finalStage: true },
      ],
    });
    setErrors({});
  };

  const updateForm = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    const checked = type === 'checkbox' ? (event.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const updateStage = (index: number, updates: Partial<WorkflowStage>) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => (i === index ? { ...stage, ...updates } : stage)),
    }));
  };

  const updateResponsibility = (index: number, updates: Partial<Responsibility>) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === index
          ? { ...stage, responsibility: { ...stage.responsibility, ...updates } }
          : stage
      ),
    }));
  };

  const addStage = () => {
    setForm((prev) => ({
      ...prev,
      stages: [
        ...prev.stages.map((stage) => ({ ...stage, finalStage: false })),
        { ...blankStage(prev.stages.length + 1), finalStage: true },
      ],
    }));
  };

  const removeStage = (index: number) => {
    setForm((prev) => {
      const stages = prev.stages.filter((_, i) => i !== index).map((stage, i, all) => ({
        ...stage,
        displayOrder: i + 1,
        finalStage: stage.finalStage && all.length > 1 ? stage.finalStage : i === all.length - 1,
      }));
      return { ...prev, stages };
    });
  };

  const addAlternate = (stageIndex: number) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => {
        if (i !== stageIndex) return stage;
        const alternates = stage.responsibility.alternates || [];
        return {
          ...stage,
          responsibility: {
            ...stage.responsibility,
            alternates: [...alternates, { active: true, priorityOrder: alternates.length + 1 }],
          },
        };
      }),
    }));
  };

  const updateAlternate = (stageIndex: number, altIndex: number, updates: Partial<Alternate>) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => {
        if (i !== stageIndex) return stage;
        return {
          ...stage,
          responsibility: {
            ...stage.responsibility,
            alternates: (stage.responsibility.alternates || []).map((alternate, j) =>
              j === altIndex ? { ...alternate, ...updates } : alternate
            ),
          },
        };
      }),
    }));
  };

  const removeAlternate = (stageIndex: number, altIndex: number) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => {
        if (i !== stageIndex) return stage;
        return {
          ...stage,
          responsibility: {
            ...stage.responsibility,
            alternates: (stage.responsibility.alternates || [])
              .filter((_, j) => j !== altIndex)
              .map((alternate, j) => ({ ...alternate, priorityOrder: j + 1 })),
          },
        };
      }),
    }));
  };

  const markFinal = (stageIndex: number) => {
    setForm((prev) => ({
      ...prev,
      stages: prev.stages.map((stage, i) => ({ ...stage, finalStage: i === stageIndex })),
    }));
  };

  const usersForRole = (role: string) =>
    activeUsers.filter((user) => String(user.userType || '').toUpperCase() === role);

  const validate = () => {
    const nextErrors: ValidationErrors = {};
    if (!form.workflowKey.trim()) nextErrors.workflowKey = 'Workflow key is required.';
    if (!form.workflowName.trim()) nextErrors.workflowName = 'Workflow name is required.';
    if (!form.stages.length) nextErrors.stages = 'At least one stage is required.';
    if (form.stages.filter((stage) => stage.finalStage).length !== 1) {
      nextErrors.finalStage = 'Select exactly one final stage.';
    }
    form.stages.forEach((stage, stageIndex) => {
      if (!stage.actorRole) nextErrors[`stage-${stageIndex}-role`] = 'Select a responsible role.';
      if (!stage.responsibility.primaryUserId) nextErrors[`stage-${stageIndex}-primary`] = 'Select a primary user.';
      const seen = new Set<number>();
      (stage.responsibility.alternates || []).forEach((alternate, altIndex) => {
        if (!alternate.alternateUserId) return;
        if (alternate.alternateUserId === stage.responsibility.primaryUserId) {
          nextErrors[`stage-${stageIndex}-alt-${altIndex}`] = 'Primary user cannot also be an alternate.';
        }
        if (seen.has(alternate.alternateUserId)) {
          nextErrors[`stage-${stageIndex}-alt-${altIndex}`] = 'Duplicate alternate user.';
        }
        seen.add(alternate.alternateUserId);
      });
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!validate() || !currentUserId) return;
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        ...form,
        applicationType: form.applicationType || null,
        facultyId: form.facultyId ? Number(form.facultyId) : null,
        departmentId: form.departmentId ? Number(form.departmentId) : null,
        changedByUserId: currentUserId,
        stages: form.stages.map((stage, index) => ({
          ...stage,
          displayOrder: index + 1,
          standardDeadlineDays: Number(stage.standardDeadlineDays || 14),
          responsibility: {
            ...stage.responsibility,
            alternates: (stage.responsibility.alternates || []).map((alternate, altIndex) => ({
              ...alternate,
              priorityOrder: altIndex + 1,
              active: alternate.active !== false,
            })),
          },
        })),
      };
      const response = await fetch('/v1/api/workflow-configurations/versions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(await getErrorMessage(response));
      const result = (await response.json()) as ApiResponse<WorkflowConfigurationDto>;
      setMessage('Workflow configuration version saved.');
      await load();
      selectConfiguration(result.data);
    } catch (err: any) {
      setMessage(err?.message || 'Workflow configuration could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <main className="wfc-wrap">
        <section className="wfc-empty">
          <FaUserPlus aria-hidden="true" />
          <h1>Workflow Configuration</h1>
          <p>You do not have permission to manage workflow configuration.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="wfc-wrap">
      <header className="wfc-header">
        <div>
          <h1>Workflow Configuration</h1>
          <p>Manage staged responsibilities, alternates, deadlines and workflow versions.</p>
        </div>
        <div className="wfc-actions">
          <button type="button" className="wfc-btn wfc-btn--ghost" onClick={applyGenderTemplate}>
            <FaClipboardList /> Gender Institute
          </button>
          <button type="button" className="wfc-btn wfc-btn--primary" onClick={createNew}>
            <FaPlus /> New Workflow
          </button>
        </div>
      </header>

      {message && <div className="wfc-alert" role="status">{message}</div>}

      <div className="wfc-grid">
        <aside className="wfc-panel wfc-list-panel">
          <label className="wfc-search">
            <FaFilter aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search workflows"
              aria-label="Search workflows"
            />
          </label>
          <div className="wfc-config-list" aria-busy={loading}>
            {loading ? (
              <div className="wfc-muted">Loading workflow configurations...</div>
            ) : filteredConfigurations.length === 0 ? (
              <div className="wfc-muted">No workflow configurations found.</div>
            ) : (
              filteredConfigurations.map((configuration) => (
                <button
                  type="button"
                  key={`${configuration.workflowKey}-${configuration.workflowVersion}`}
                  className={`wfc-config-card ${selectedKey === configuration.workflowKey ? 'active' : ''}`}
                  onClick={() => selectConfiguration(configuration)}
                >
                  <span>{configuration.workflowName || configuration.workflowKey}</span>
                  <small>
                    Version {configuration.workflowVersion || 1} · {configuration.scopeType || 'GLOBAL'}
                  </small>
                  <em>{configuration.active ? 'Active' : 'Inactive'}</em>
                </button>
              ))
            )}
          </div>
        </aside>

        <form className="wfc-panel wfc-editor" onSubmit={save}>
          <div className="wfc-section-title">
            <h2>{form.workflowKey ? `${form.workflowKey} configuration` : 'New workflow'}</h2>
            <button type="submit" className="wfc-btn wfc-btn--primary" disabled={saving}>
              <FaSyncAlt /> {saving ? 'Saving...' : 'Save New Version'}
            </button>
          </div>

          <div className="wfc-form-grid">
            <label>
              <span>Workflow name</span>
              <input aria-label="workflow Name" name="workflowName" value={form.workflowName} onChange={updateForm} />
              {errors.workflowName && <small className="wfc-error">{errors.workflowName}</small>}
            </label>
            <label>
              <span>Workflow key</span>
              <input aria-label="workflow Key" name="workflowKey" value={form.workflowKey} onChange={updateForm} />
              {errors.workflowKey && <small className="wfc-error">{errors.workflowKey}</small>}
            </label>
            <label>
              <span>Application type</span>
              <select aria-label="application Type" name="applicationType" value={form.applicationType || ''} onChange={updateForm}>
                {APPLICATION_TYPES.map((type) => <option key={type || 'ANY'} value={type}>{type || 'Any'}</option>)}
              </select>
            </label>
            <label>
              <span>Scope</span>
              <select aria-label="scope Type" name="scopeType" value={form.scopeType} onChange={updateForm}>
                {SCOPE_OPTIONS.map((scope) => <option key={scope} value={scope}>{scope}</option>)}
              </select>
            </label>
            <label>
              <span>Institute code</span>
              <input aria-label="GENDER_INSTITUTE" name="instituteCode" value={form.instituteCode || ''} onChange={updateForm} placeholder="GENDER_INSTITUTE" />
            </label>
            <label>
              <span>Committee code</span>
              <input aria-label="committee Code" name="committeeCode" value={form.committeeCode || ''} onChange={updateForm} />
            </label>
            <label>
              <span>Resubmission behaviour</span>
              <select aria-label="resubmission Policy" name="resubmissionPolicy" value={form.resubmissionPolicy || 'RETURN_TO_REJECTING_STAGE'} onChange={updateForm}>
                {RESUBMISSION_POLICIES.map((policy) => <option key={policy} value={policy}>{policy.replaceAll('_', ' ')}</option>)}
              </select>
            </label>
            <label className="wfc-check">
              <input aria-label="active" type="checkbox" name="active" checked={form.active !== false} onChange={updateForm} />
              <span>Active configuration</span>
            </label>
          </div>

          <section className="wfc-stages" aria-label="Workflow stages">
            <div className="wfc-section-title">
              <h2>Ordered Stages</h2>
              <button type="button" className="wfc-btn wfc-btn--ghost" onClick={addStage}>
                <FaPlus /> Add Stage
              </button>
            </div>
            {errors.finalStage && <div className="wfc-error">{errors.finalStage}</div>}
            {form.stages.map((stage, stageIndex) => {
              const eligible = usersForRole(stage.actorRole);
              const alternates = stage.responsibility.alternates || [];
              return (
                <article className="wfc-stage" key={stageIndex}>
                  <div className="wfc-stage-header">
                    <strong>Stage {stageIndex + 1}</strong>
                    <div>
                      <button type="button" className="wfc-link-btn" onClick={() => markFinal(stageIndex)}>
                        {stage.finalStage ? 'Final stage' : 'Mark final'}
                      </button>
                      {form.stages.length > 1 && (
                        <button type="button" className="wfc-link-btn danger" onClick={() => removeStage(stageIndex)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="wfc-form-grid">
                    <label>
                      <span>Stage name</span>
                      <input value={stage.name || ''} onChange={(e) => updateStage(stageIndex, { name: e.target.value })} />
                    </label>
                    <label>
                      <span>Responsible role</span>
                      <select
                        value={stage.actorRole}
                        onChange={(e) => updateStage(stageIndex, {
                          actorRole: e.target.value,
                          responsibility: { active: true, alternatesMayActWhenPrimaryActive: false, alternates: [] },
                        })}
                      >
                        {ROLE_OPTIONS.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>Action type</span>
                      <select
                        value={stage.stageActionType}
                        onChange={(e) => updateStage(stageIndex, { stageActionType: e.target.value as WorkflowStage['stageActionType'] })}
                      >
                        <option value="RECOMMENDATION">Recommendation</option>
                        <option value="APPROVAL">Approval</option>
                      </select>
                    </label>
                    <label>
                      <span>Deadline days</span>
                      <input
                        type="number"
                        min={1}
                        value={stage.standardDeadlineDays}
                        onChange={(e) => updateStage(stageIndex, { standardDeadlineDays: Number(e.target.value) })}
                      />
                    </label>
                    <label>
                      <span>Primary user</span>
                      <select
                        value={stage.responsibility.primaryUserId || ''}
                        onChange={(e) => updateResponsibility(stageIndex, { primaryUserId: Number(e.target.value) || undefined })}
                      >
                        <option value="">Select active {roleLabel(stage.actorRole)}</option>
                        {eligible.map((user) => <option key={user.id} value={user.id}>{userLabel(user)}</option>)}
                      </select>
                      {errors[`stage-${stageIndex}-primary`] && <small className="wfc-error">{errors[`stage-${stageIndex}-primary`]}</small>}
                    </label>
                    <label className="wfc-check">
                      <input
                        type="checkbox"
                        checked={stage.responsibility.alternatesMayActWhenPrimaryActive === true}
                        onChange={(e) => updateResponsibility(stageIndex, { alternatesMayActWhenPrimaryActive: e.target.checked })}
                      />
                      <span>Alternates may act while primary is active</span>
                    </label>
                  </div>

                  <div className="wfc-alt-header">
                    <strong>Alternates</strong>
                    <button type="button" className="wfc-btn wfc-btn--ghost wfc-btn--small" onClick={() => addAlternate(stageIndex)}>
                      <FaPlus /> Add Alternate
                    </button>
                  </div>
                  {alternates.length === 0 ? (
                    <div className="wfc-muted">No alternates assigned.</div>
                  ) : (
                    alternates.map((alternate, altIndex) => (
                      <div className="wfc-alt-row" key={altIndex}>
                        <select
                          value={alternate.alternateUserId || ''}
                          onChange={(e) => updateAlternate(stageIndex, altIndex, { alternateUserId: Number(e.target.value) || undefined })}
                        >
                          <option value="">Select alternate</option>
                          {eligible.map((user) => <option key={user.id} value={user.id}>{userLabel(user)}</option>)}
                        </select>
                        <label className="wfc-mini-check">
                          <input
                            type="checkbox"
                            checked={alternate.active !== false}
                            onChange={(e) => updateAlternate(stageIndex, altIndex, { active: e.target.checked })}
                          />
                          Active
                        </label>
                        <button type="button" className="wfc-icon-btn" onClick={() => removeAlternate(stageIndex, altIndex)} aria-label="Remove alternate">
                          <FaTimes />
                        </button>
                        {errors[`stage-${stageIndex}-alt-${altIndex}`] && <small className="wfc-error">{errors[`stage-${stageIndex}-alt-${altIndex}`]}</small>}
                      </div>
                    ))
                  )}
                </article>
              );
            })}
          </section>

          <section className="wfc-audit">
            <h2><FaClipboardList /> Change History</h2>
            {audit.length === 0 ? (
              <div className="wfc-muted">No audit history recorded for this workflow.</div>
            ) : (
              audit.slice(0, 6).map((row) => (
                <div className="wfc-audit-row" key={row.id}>
                  <span>Version {row.workflowVersion}</span>
                  <strong>{row.changeType}</strong>
                  <small>{row.changedByUserName || 'System'} · {row.changedAt ? new Date(row.changedAt).toLocaleString() : ''}</small>
                </div>
              ))
            )}
          </section>
        </form>
      </div>
    </main>
  );
};

export default WorkflowConfiguration;
